import * as Sentry from '@sentry/react';
import {createStore, del, get, set} from 'idb-keyval';
import {compressForStorage, decompressFromStorage, formatBytes, checkStorageQuota} from './utils/dataCompression';
import LocalStorageWorker from './localStorage.worker.wrapper';

export const STORAGE_KEYS = {
	QUERY_CACHE: 'FACTORIO_PRINTS_QUERY_CACHE',
	CREATE_FORM: 'factorio-blueprint-create-form',
	HIGH_WATERMARK: 'factorio-prints-high-watermark',
} as const;

export const CACHE_BUSTER = '7';

export const indexedDbStore = createStore('factorio-prints-db', 'query-cache-store');

interface DebounceOptions {
	leading?: boolean;
	trailing?: boolean;
	maxWait?: number;
}

interface DebouncedFunction<T extends (...args: any[]) => any> {
	(...args: Parameters<T>): ReturnType<T>;
	cancel(): void;
	flush(): ReturnType<T>;
	pending(): boolean;
}

function debounce<T extends (...args: any[]) => any>(
	func: T,
	wait: number,
	options: DebounceOptions = {},
): DebouncedFunction<T> {
	let lastArgs: Parameters<T> | undefined;
	let lastThis: ThisParameterType<T> | undefined;
	let maxWait: number;
	let result: ReturnType<T>;
	let timerId: ReturnType<typeof setTimeout> | undefined;
	let lastCallTime: number | undefined;
	let lastInvokeTime = 0;
	const leading = !!options.leading;
	const trailing = 'trailing' in options ? !!options.trailing : true;
	const maxing = 'maxWait' in options;
	maxWait = maxing ? Math.max(options.maxWait || 0, wait) : 0;

	function invokeFunc(time: number): ReturnType<T> {
		const args = lastArgs!;
		const thisArg = lastThis;

		lastArgs = lastThis = undefined;
		lastInvokeTime = time;
		if (!args) {
			throw new Error('invokeFunc called without arguments');
		}
		result = func.apply(thisArg, args);
		return result;
	}

	function startTimer(pendingFunc: () => void, wait: number): ReturnType<typeof setTimeout> {
		return setTimeout(pendingFunc, wait);
	}

	function cancelTimer(id: ReturnType<typeof setTimeout>): void {
		clearTimeout(id);
	}

	function shouldInvoke(time: number): boolean {
		const timeSinceLastCall = time - (lastCallTime || 0);
		const timeSinceLastInvoke = time - lastInvokeTime;

		return (
			lastCallTime === undefined ||
			timeSinceLastCall >= wait ||
			timeSinceLastCall < 0 ||
			(maxing && timeSinceLastInvoke >= maxWait)
		);
	}

	function trailingEdge(time: number): ReturnType<T> {
		timerId = undefined;

		if (trailing && lastArgs) {
			return invokeFunc(time);
		}
		lastArgs = lastThis = undefined;
		return result;
	}

	function leadingEdge(time: number): ReturnType<T> {
		lastInvokeTime = time;
		timerId = startTimer(timerExpired, wait);
		return leading ? invokeFunc(time) : result;
	}

	function remainingWait(time: number): number {
		const timeSinceLastCall = time - (lastCallTime || 0);
		const timeSinceLastInvoke = time - lastInvokeTime;
		const timeWaiting = wait - timeSinceLastCall;

		return maxing ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke) : timeWaiting;
	}

	function timerExpired(): void {
		const time = Date.now();

		if (shouldInvoke(time)) {
			trailingEdge(time);
			return;
		}

		timerId = startTimer(timerExpired, remainingWait(time));
	}

	function debounced(this: ThisParameterType<T>, ...args: Parameters<T>): ReturnType<T> {
		const time = Date.now();
		const isInvoking = shouldInvoke(time);

		lastArgs = args;
		lastThis = this; // eslint-disable-line @typescript-eslint/no-this-alias
		lastCallTime = time;

		if (isInvoking) {
			if (timerId === undefined) {
				return leadingEdge(lastCallTime);
			}
			if (maxing) {
				timerId = startTimer(timerExpired, wait);
				return invokeFunc(lastCallTime);
			}
		}
		if (timerId === undefined) {
			timerId = startTimer(timerExpired, wait);
		}
		return result;
	}

	debounced.cancel = (): void => {
		if (timerId !== undefined) {
			cancelTimer(timerId);
		}
		lastInvokeTime = 0;
		lastArgs = lastCallTime = lastThis = timerId = undefined;
	};

	debounced.flush = (): ReturnType<T> => (timerId === undefined ? result : trailingEdge(Date.now()));

	debounced.pending = (): boolean => timerId !== undefined;

	return debounced;
}

let worker: Worker | undefined;
let operationCounter = 0;
const pendingOperations = new Map();
let workerReconnectAttempts = 0;
const maxWorkerReconnectAttempts = 5;
let lastWorkerResetTime = 0;
const workerResetCooldown = 30000;
let isWorkerInitializing = false;
let workerInitPromise: Promise<void> | undefined;

function resetWorkerIfNeeded() {
	const now = Date.now();
	if (now - lastWorkerResetTime > workerResetCooldown) {
		workerReconnectAttempts = 0;
		lastWorkerResetTime = now;
	}
}

function terminateWorker() {
	if (worker) {
		try {
			worker.terminate();
		} catch (error) {
			console.warn('[IndexedDB Worker] Error terminating worker:', error);
		}
		worker = undefined;
	}
	isWorkerInitializing = false;
	workerInitPromise = undefined;
}

async function getWorker(): Promise<Worker | undefined> {
	// If worker is being initialized, wait for it
	if (isWorkerInitializing && workerInitPromise) {
		try {
			await workerInitPromise;
			return worker;
		} catch (error) {
			console.error('[IndexedDB Worker] Failed to wait for worker initialization:', error);
			return undefined;
		}
	}

	if (!worker && !isWorkerInitializing) {
		resetWorkerIfNeeded();

		if (workerReconnectAttempts >= maxWorkerReconnectAttempts) {
			return undefined;
		}

		isWorkerInitializing = true;

		workerInitPromise = new Promise<void>((resolve, reject) => {
			try {
				workerReconnectAttempts++;
				console.log(`[IndexedDB Worker] Creating worker (attempt ${workerReconnectAttempts})`);

				worker = new LocalStorageWorker();
				console.log('[IndexedDB Worker] Successfully created worker using Vite import');

				worker.onerror = (event) => {
					let errorToCapture: Error | null;
					let errorMessage: string;

					// Handle null/undefined event (shouldn't happen but being defensive)
					if (!event) {
						errorMessage = 'Worker error: null or undefined event';
						console.error('[IndexedDB Worker] Received null/undefined error event');
						errorToCapture = new Error(errorMessage);
						errorToCapture.name = 'WorkerError';

						Sentry.captureException(errorToCapture, {
							tags: {
								component: 'indexeddb-worker',
								operation: 'worker-error',
								reconnectAttempt: workerReconnectAttempts,
							},
							extra: {
								eventIsNull: true,
							},
						});

						if (worker) {
							worker.terminate();
							worker = undefined;
						}

						pendingOperations.forEach((op) => {
							op.reject(errorToCapture);
						});
						pendingOperations.clear();

						return;
					}

					if (event instanceof ErrorEvent) {
						errorMessage = event.message || 'Unknown worker error';
						console.error('[IndexedDB Worker] Worker error:', errorMessage);
						if (event.error instanceof Error) {
							errorToCapture = event.error;
						} else {
							errorToCapture = new Error(errorMessage);
							errorToCapture.name = 'WorkerError';
						}

						Sentry.captureException(errorToCapture, {
							tags: {
								component: 'indexeddb-worker',
								operation: 'worker-error',
								reconnectAttempt: workerReconnectAttempts,
							},
							extra: {
								filename: event.filename,
								lineno: event.lineno,
								colno: event.colno,
								message: errorMessage,
								errorType: event.error ? event.error.constructor.name : 'Unknown',
							},
						});
					} else {
						// Safari sometimes emits a plain Event instead of ErrorEvent
						const genericEvent = event as Event;
						const eventType =
							genericEvent && typeof genericEvent === 'object' && genericEvent !== null
								? Object.prototype.toString.call(genericEvent).slice(8, -1)
								: 'Unknown';

						// Detect Safari browser
						const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

						// Extract any available properties from the event
						const eventProperties: Record<string, unknown> = {};
						if (genericEvent && typeof genericEvent === 'object') {
							// Attempt to extract standard Event properties
							if ('type' in genericEvent) eventProperties.type = genericEvent.type;
							if ('target' in genericEvent) eventProperties.hasTarget = genericEvent.target !== null;
							if ('currentTarget' in genericEvent)
								eventProperties.hasCurrentTarget = genericEvent.currentTarget !== null;
							if ('defaultPrevented' in genericEvent)
								eventProperties.defaultPrevented = genericEvent.defaultPrevented;
							if ('bubbles' in genericEvent) eventProperties.bubbles = genericEvent.bubbles;
							if ('cancelable' in genericEvent) eventProperties.cancelable = genericEvent.cancelable;
							if ('timeStamp' in genericEvent) eventProperties.timeStamp = genericEvent.timeStamp;
							if ('isTrusted' in genericEvent) eventProperties.isTrusted = genericEvent.isTrusted;

							// Try to get any custom properties
							try {
								const allKeys = Object.keys(genericEvent);
								if (allKeys.length > 0) {
									eventProperties.availableKeys = allKeys;
								}
							} catch {
								// Ignore if we can't enumerate keys
							}
						}

						// Create a more descriptive error message
						if (isSafari && eventType === 'Event') {
							errorMessage = `Safari worker initialization error: The worker script may have failed to load or encountered a syntax error`;
						} else {
							errorMessage = `Worker error (${eventType}): Unable to extract error details from event object`;
						}

						// Log Safari worker issues as info level since they're expected
						if (isSafari && eventType === 'Event') {
							if (typeof Sentry !== 'undefined') {
								Sentry.addBreadcrumb({
									message: 'Safari worker initialization issue',
									category: 'indexeddb',
									level: 'info',
									data: {
										eventType,
										eventProperties,
										userAgent: navigator.userAgent,
									},
								});
							}
							// Don't capture Safari worker init issues as exceptions
							errorToCapture = null;
						} else {
							console.error('[IndexedDB Worker] Worker error:', errorMessage, {
								eventType,
								eventProperties,
								isSafari,
								userAgent: navigator.userAgent,
							});
							errorToCapture = new Error(errorMessage);
							errorToCapture.name = 'WorkerError';
						}

						if (errorToCapture) {
							Sentry.captureException(errorToCapture, {
								tags: {
									component: 'indexeddb-worker',
									operation: 'worker-error',
									reconnectAttempt: workerReconnectAttempts,
									browser: isSafari ? 'safari' : 'other',
								},
								extra: {
									eventType,
									eventProperties,
									eventString: String(event),
									isSafari,
									userAgent: navigator.userAgent,
									currentUrl: window.location.href,
								},
							});
						}
					}

					terminateWorker();
					reject(new Error(errorMessage));

					pendingOperations.forEach((op) => {
						// Resolve with appropriate fallback based on operation type
						if (op.operationType === 'get') {
							op.resolve({data: undefined});
						} else {
							op.resolve({success: false});
						}
					});
					pendingOperations.clear();
				};

				worker.onmessage = (e) => {
					const {id, result, error, success} = e.data;

					if (e.data.type === 'error' && error?.isUncaught) {
						const uncaughtError = new Error(error.message);
						uncaughtError.name = 'WorkerUncaughtError';

						Sentry.captureException(uncaughtError, {
							tags: {
								component: 'indexeddb-worker',
								operation: 'worker-runtime',
								errorType: 'uncaught-error',
							},
							extra: {
								details: error.details,
								stack: error.stack,
							},
						});
						return;
					}

					if (e.data.type === 'error' && error?.isUnhandledRejection) {
						const rejectionError = new Error(error.message);
						rejectionError.name = 'WorkerUnhandledRejection';

						Sentry.captureException(rejectionError, {
							tags: {
								component: 'indexeddb-worker',
								operation: 'worker-runtime',
								errorType: 'unhandled-rejection',
							},
							extra: {
								reason: error.reason,
								stack: error.stack,
							},
						});
						return;
					}

					const pendingOp = pendingOperations.get(id);

					if (pendingOp) {
						if (success) {
							pendingOp.resolve(result);
						} else {
							if (error?.isConnectionClosing) {
								// Connection lost - reset worker for next operation
								terminateWorker();
								console.warn(
									'[IndexedDB] Operation failed due to closing connection, resolving with undefined',
								);

								// 📊 Log as breadcrumb for debugging (error is already handled gracefully)
								Sentry.addBreadcrumb({
									message: 'IndexedDB connection closing - handled gracefully',
									category: 'indexeddb',
									level: 'warning',
									data: {
										errorMessage: error.message,
										operationType: e.data.type,
										key: e.data.key,
									},
								});
								// Return a safe fallback result based on the operation type
								pendingOp.resolve(
									pendingOp.operationType === 'get' ? {data: undefined} : {success: false},
								);
							} else {
								// 🛡️ Handle blob write failures specifically
								const isBlobWriteError =
									error.message.includes('Failed to write blobs') ||
									error.message.includes('IOError') ||
									error.message.includes('blob') ||
									error.message.includes('Blob');

								if (isBlobWriteError) {
									console.warn('[IndexedDB] Blob write failure detected:', error.message);

									// 📊 Log to Sentry for monitoring with specific context
									try {
										const blobError = new Error(`IndexedDB blob write failed: ${error.message}`);
										blobError.name = 'IndexedDBBlobWriteError';

										Sentry.captureException(blobError, {
											level: 'warning',
											tags: {
												component: 'localStorage',
												errorType: 'blob-write-failure',
												operationType: e.data.type || 'unknown',
											},
											extra: {
												errorMessage: error.message,
												key: e.data.key,
												dataSize: e.data.data ? JSON.stringify(e.data.data).length : 0,
											},
										});
									} catch (sentryError) {
										console.warn('[IndexedDB] Failed to log blob error to Sentry:', sentryError);
									}
								} else {
									// For other errors, log but don't throw
									console.warn('[IndexedDB] Operation failed, using fallback:', error.message);
								}

								// Always resolve with fallback instead of rejecting to prevent cache persistence failure
								// This allows the app to continue functioning even if caching fails
								pendingOp.resolve(
									pendingOp.operationType === 'get' ? {data: undefined} : {success: false},
								);
							}
						}
						pendingOperations.delete(id);
					}
				};

				// Wait for initialization confirmation
				const initTimeout = setTimeout(() => {
					console.error('[IndexedDB Worker] Worker initialization timeout');
					terminateWorker();
					reject(new Error('Worker initialization timeout'));
				}, 5000);

				const originalOnMessage = worker.onmessage;
				worker.onmessage = (e) => {
					if (!e.data.id && e.data.success) {
						clearTimeout(initTimeout);
						worker!.onmessage = originalOnMessage;
						workerReconnectAttempts = 0;
						isWorkerInitializing = false;
						console.log('[IndexedDB Worker] Worker initialized successfully');
						resolve();
					} else if (originalOnMessage && worker) {
						originalOnMessage.call(worker, e);
					}
				};

				worker.postMessage({
					type: 'init',
					storeConfig: {
						dbName: 'factorio-prints-db',
						storeName: 'query-cache-store',
					},
				});
			} catch (error) {
				console.error('[IndexedDB Worker] Failed to create worker:', error);
				terminateWorker();

				Sentry.captureException(error, {
					tags: {
						component: 'indexeddb-worker',
						operation: 'worker-creation',
						reconnectAttempt: workerReconnectAttempts,
					},
				});

				reject(error);
			}
		});

		try {
			await workerInitPromise;
		} catch (error) {
			console.error('[IndexedDB Worker] Worker initialization failed:', error);
			return undefined;
		}
	}

	return worker;
}

type WorkerOperationResult = {success: true; data?: any} | {success: false} | {data: any};

async function workerOperation(
	type: string,
	key: string,
	data: any = null,
	retryCount = 0,
): Promise<WorkerOperationResult> {
	const maxRetries = type === 'set' ? 3 : 4;
	const baseDelay = type === 'set' ? 1000 : 500;

	try {
		const worker = await getWorker();

		if (!worker) {
			try {
				switch (type) {
					case 'set':
						await set(key, data, indexedDbStore);
						return {success: true};
					case 'get':
						return {data: await get(key, indexedDbStore)};
					case 'delete':
						await del(key, indexedDbStore);
						return {success: true};
					default:
						throw new Error('Unknown operation type');
				}
			} catch (error) {
				if (
					error instanceof Error &&
					(error.message.includes('Connection to Indexed Database server lost') ||
						error.message.includes('database connection is closing') ||
						error.message.includes('IDBDatabase') ||
						error.message.includes('TransactionInactiveError') ||
						error.message.includes('AbortError'))
				) {
					if (retryCount < maxRetries) {
						const delay = baseDelay * Math.pow(2, retryCount);
						console.warn(
							`[IndexedDB] Connection lost, retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`,
						);
						await new Promise((resolve) => setTimeout(resolve, delay));
						return workerOperation(type, key, data, retryCount + 1);
					}

					console.error('[IndexedDB] Connection lost after max retries, returning fallback');
					return type === 'get' ? {data: undefined} : {success: false};
				}
				throw error;
			}
		}

		return new Promise((resolve, reject) => {
			const id = operationCounter++;

			pendingOperations.set(id, {resolve, reject, operationType: type});

			worker.postMessage({type, key, data, id});

			const baseTimeout = type === 'set' ? 30000 : type === 'delete' ? 5000 : 20000;
			const retryMultiplier = Math.min(retryCount + 1, 3);
			const timeoutDuration = baseTimeout * retryMultiplier;
			const startTime = Date.now();

			if (retryCount > 0) {
				console.log(
					`[IndexedDB] Operation ${type} with retry ${retryCount}, timeout increased to ${timeoutDuration}ms`,
				);
			}

			setTimeout(() => {
				if (pendingOperations.has(id)) {
					pendingOperations.delete(id);
					const duration = Date.now() - startTime;

					// Log timeout errors to Sentry with rate limiting
					try {
						const expectedMaxDuration = timeoutDuration + 5000;
						const isExpectedTimeout = duration < expectedMaxDuration;

						if (!isExpectedTimeout) {
							const timeoutError = new Error(
								`IndexedDB operation timeout: ${type} ${key} after ${duration}ms`,
							);
							timeoutError.name = 'IndexedDBTimeoutError';

							Sentry.captureException(timeoutError, {
								level: duration > 60000 ? 'error' : 'warning',
								tags: {
									component: 'localStorage',
									errorType: 'timeout',
									operationType: type,
									database: 'FACTORIO_PRINTS_QUERY_CACHE',
									timeoutRange:
										duration < 20000
											? '10-20s'
											: duration < 40000
												? '20-40s'
												: duration < 60000
													? '40-60s'
													: '60s+',
								},
								extra: {
									operationType: type,
									key: key,
									configuredTimeout: timeoutDuration,
									baseTimeout: baseTimeout,
									retryMultiplier: retryMultiplier,
									actualDuration: duration,
									retryCount: retryCount,
									maxRetries: maxRetries,
									userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
								},
							});
						} else {
							console.warn(
								`[IndexedDB] Operation timeout (expected range): ${type} ${key} after ${duration}ms`,
							);
						}
					} catch (sentryError) {
						// Silently ignore Sentry errors to prevent secondary errors
						console.warn('[IndexedDB] Failed to log timeout to Sentry:', sentryError);
					}

					if (type === 'get' && key === STORAGE_KEYS.QUERY_CACHE) {
						del(key, indexedDbStore).catch((err) =>
							console.error('[IndexedDB] Failed to clear cache after timeout:', err),
						);
					}

					resolve(type === 'get' ? {data: undefined} : {success: false});
				}
			}, timeoutDuration);
		});
	} catch (error) {
		if (
			error instanceof Error &&
			(error.message.includes('Connection to Indexed Database server lost') ||
				error.message.includes('Failed to execute') ||
				error.message.includes('InvalidStateError') ||
				error.message.includes('TransactionInactiveError') ||
				error.message.includes('AbortError'))
		) {
			if (retryCount < maxRetries) {
				const delay = baseDelay * Math.pow(2, retryCount);
				console.warn(
					`[IndexedDB] Worker operation failed, retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`,
				);
				// Reset worker to force reconnection
				terminateWorker();
				await new Promise((resolve) => setTimeout(resolve, delay));
				return workerOperation(type, key, data, retryCount + 1);
			}
		}

		console.error('[IndexedDB] Worker operation failed:', error);
		Sentry.captureException(error, {
			tags: {
				component: 'localStorage',
				errorType: 'worker-operation-error',
				operationType: type,
			},
			extra: {
				operationType: type,
				key: key,
				retryCount: retryCount,
				errorMessage: error instanceof Error ? error.message : String(error),
			},
		});

		return type === 'get' ? {data: undefined} : {success: false};
	}
}

interface Persister {
	persistClient: (client: any) => Promise<void>;
	restoreClient: () => Promise<any>;
	removeClient: () => Promise<void>;
}

export function createIDBPersister(idbValidKey: string = STORAGE_KEYS.QUERY_CACHE): Persister {
	let lastPersistedData: string | null = null;

	const extractQueryData = (client: any): any => {
		if (!client || !client.clientState) {
			return client;
		}

		const dataOnly = {
			queries: client.clientState.queries?.map((query: any) => ({
				queryKey: query.queryKey,
				queryHash: query.queryHash,
				data: query.state?.data,
				...(query.state?.error ? {error: query.state.error} : {}),
			})),
			mutations: client.clientState.mutations?.map((mutation: any) => ({
				mutationKey: mutation.mutationKey,
				state: mutation.state?.status,
				data: mutation.state?.data,
				error: mutation.state?.error,
			})),
		};

		return dataOnly;
	};

	const debouncedPersist = debounce(
		async (client: any) => {
			try {
				const dataOnly = extractQueryData(client);
				const currentData = JSON.stringify(dataOnly);

				if (lastPersistedData === currentData) {
					console.log('[IndexedDB] Cache state unchanged, skipping persistence');
					return;
				}

				const compressedData = compressForStorage(client);
				const originalSize = JSON.stringify(client).length;
				const compressedSize = compressedData.data.length;

				if (compressedData.compressed) {
					console.log(
						`[IndexedDB] Compressed cache: ${formatBytes(originalSize)} → ${formatBytes(compressedSize)}`,
					);
				}

				const quotaCheck = await checkStorageQuota(compressedSize);
				if (!quotaCheck.hasSpace) {
					console.warn('[IndexedDB] Insufficient storage space available');

					const quotaError = new Error(
						`IndexedDB quota exceeded: need ${formatBytes(compressedSize)}, available ${formatBytes(quotaCheck.available || 0)}`,
					);
					quotaError.name = 'IndexedDBQuotaError';

					Sentry.captureException(quotaError, {
						level: 'warning',
						tags: {
							component: 'localStorage',
							errorType: 'quota-exceeded',
						},
						extra: {
							requiredSize: compressedSize,
							availableSpace: quotaCheck.available,
							usedSpace: quotaCheck.used,
						},
					});

					console.log('[IndexedDB] Attempting to clear old cache data');
					await workerOperation('delete', idbValidKey);

					const quotaCheckAfterClear = await checkStorageQuota(compressedSize);
					if (!quotaCheckAfterClear.hasSpace) {
						console.error('[IndexedDB] Still insufficient space after clearing cache');
						return;
					}
				}

				const result = await workerOperation('set', idbValidKey, compressedData);

				if (result && 'success' in result && !result.success) {
					// Silent failure - app continues without persistence
					// Add breadcrumb for debugging if needed
					if (typeof Sentry !== 'undefined') {
						Sentry.addBreadcrumb({
							message: 'IndexedDB persistence failed gracefully',
							category: 'indexeddb',
							level: 'info',
							data: {key: idbValidKey},
						});
					}
				} else {
					lastPersistedData = currentData;
				}
			} catch (error) {
				console.error('[IndexedDB] Error persisting to IndexedDB:', error);

				try {
					Sentry.captureException(error, {
						level: 'warning',
						tags: {
							component: 'localStorage',
							operation: 'persist',
						},
						extra: {
							key: idbValidKey,
							errorMessage: error instanceof Error ? error.message : String(error),
						},
					});
				} catch (sentryError) {
					console.warn('[IndexedDB] Failed to log to Sentry:', sentryError);
				}
			}
		},
		2000,
		{maxWait: 10000},
	);

	const persister: Persister = {
		persistClient: async (client: any) => {
			if (!client) {
				console.warn('[IndexedDB] Attempted to persist null/undefined client');
				return;
			}
			try {
				return await debouncedPersist(client);
			} catch (error) {
				console.warn('[IndexedDB] Failed to persist client:', error);
			}
		},
		restoreClient: async () => {
			try {
				const startTime = Date.now();

				if (typeof performance !== 'undefined' && performance.mark) {
					performance.mark('indexeddb-restore-start');
				}

				const result = (await workerOperation('get', idbValidKey)) as {data?: any} | undefined;
				const fetchDuration = Date.now() - startTime;

				if (result?.data) {
					const decompressionStart = Date.now();
					const decompressedData = decompressFromStorage(result.data);
					const decompressionDuration = Date.now() - decompressionStart;
					const totalDuration = Date.now() - startTime;

					const compressedSize =
						typeof result.data === 'object' && result.data.data
							? result.data.data.length
							: JSON.stringify(result.data).length;
					const dataSize = JSON.stringify(decompressedData).length;
					const formattedSize = formatBytes(dataSize);
					const formattedCompressedSize = formatBytes(compressedSize);

					if (typeof performance !== 'undefined' && performance.mark) {
						performance.mark('indexeddb-restore-end');
						try {
							performance.measure(
								'indexeddb-restore',
								'indexeddb-restore-start',
								'indexeddb-restore-end',
							);
						} catch {
							// Ignore performance API errors
						}
					}

					if (totalDuration > 2000 || dataSize > 1048576) {
						console.log(
							`[IndexedDB] Restore performance: Total ${totalDuration}ms (Fetch: ${fetchDuration}ms, Decompress: ${decompressionDuration}ms), ` +
								`Size: ${formattedCompressedSize} → ${formattedSize}`,
						);
					}

					if (totalDuration > 5000) {
						try {
							const slowRestoreError = new Error(
								`IndexedDB slow restore: took ${totalDuration}ms to restore ${formattedSize}`,
							);
							slowRestoreError.name = 'IndexedDBSlowRestoreError';

							Sentry.captureException(slowRestoreError, {
								level: 'info',
								tags: {
									component: 'localStorage',
									errorType: 'slow-restore',
								},
								extra: {
									totalDuration: totalDuration,
									fetchDuration: fetchDuration,
									decompressionDuration: decompressionDuration,
									dataSize: dataSize,
									compressedSize: compressedSize,
									formattedSize: formattedSize,
									formattedCompressedSize: formattedCompressedSize,
									compressionRatio:
										compressedSize > 0 ? (dataSize / compressedSize).toFixed(2) : 'N/A',
								},
							});
						} catch (sentryError) {
							// Silently ignore Sentry errors to prevent secondary errors
							console.warn('[IndexedDB] Failed to log slow restore to Sentry:', sentryError);
						}
					}

					return decompressedData;
				}
				return undefined;
			} catch (error) {
				console.error('[IndexedDB] Error restoring from IndexedDB:', error);

				try {
					Sentry.captureException(error, {
						tags: {
							component: 'localStorage',
							errorType: 'restore-error',
						},
						extra: {
							key: idbValidKey,
						},
					});
				} catch (sentryError) {
					console.warn('[IndexedDB] Failed to log to Sentry:', sentryError);
				}

				try {
					await workerOperation('delete', idbValidKey);
				} catch (deleteError) {
					console.error('[IndexedDB] Failed to clear cache:', deleteError);
				}

				return undefined;
			}
		},
		removeClient: async () => {
			try {
				await workerOperation('delete', idbValidKey);
				lastPersistedData = null;
			} catch (error) {
				console.error('[IndexedDB] Error removing from IndexedDB:', error);
			}
		},
	};

	// Validate the persister object before returning
	if (!persister.restoreClient || typeof persister.restoreClient !== 'function') {
		throw new Error('[IndexedDB] Failed to create valid persister - restoreClient method missing');
	}

	return persister;
}

export const saveToStorage = (key: string, data: any, retryWithoutBlueprintString = true): boolean => {
	try {
		const serializedData = JSON.stringify(data);
		localStorage.setItem(key, serializedData);
		return true;
	} catch (error) {
		if (error instanceof DOMException && error.name === 'QuotaExceededError' && retryWithoutBlueprintString) {
			if (data && typeof data === 'object' && 'blueprintString' in data) {
				const dataWithoutBlueprintString = {...data};
				delete dataWithoutBlueprintString.blueprintString;
				return saveToStorage(key, dataWithoutBlueprintString, false);
			}
		}

		console.error('Error saving to localStorage:', error);
		return false;
	}
};

export const loadFromStorage = <T = any>(key: string, defaultValue: T | null = null): T | null => {
	try {
		const serializedData = localStorage.getItem(key);
		if (serializedData === null) {
			return defaultValue;
		}
		return JSON.parse(serializedData) as T;
	} catch (error) {
		console.error('Error loading from localStorage:', error);
		return defaultValue;
	}
};

export const removeFromStorage = (key: string): void => {
	try {
		localStorage.removeItem(key);
	} catch (error) {
		console.error('Error removing from localStorage:', error);
	}
};

interface HighWatermarkData {
	lastUpdatedDate: number;
	lastChecked: number;
}

export const getHighWatermark = (): HighWatermarkData | null => {
	return loadFromStorage<HighWatermarkData>(STORAGE_KEYS.HIGH_WATERMARK);
};

export const setHighWatermark = (lastUpdatedDate: number): boolean => {
	const watermarkData: HighWatermarkData = {
		lastUpdatedDate,
		lastChecked: Date.now(),
	};
	return saveToStorage(STORAGE_KEYS.HIGH_WATERMARK, watermarkData);
};

export const updateHighWatermark = (lastUpdatedDate: number): boolean => {
	const currentWatermark = getHighWatermark();

	if (!currentWatermark || lastUpdatedDate > currentWatermark.lastUpdatedDate) {
		const result = setHighWatermark(lastUpdatedDate);
		return result;
	}

	return true;
};
